import React, { useState, useEffect, useRef } from "react";
import { Download, Share2, ChevronDown, X, Utensils, Clipboard, MessageCircle, RefreshCw } from "lucide-react";
import diet from "../../Assets/Images/rewamp/dashboarddiet.webp";
import { resolvePdfUrl } from "../../api/Services/resolvePdfUrl";

const renderCureBayHighlight = (text) => {
  if (!text || typeof text !== "string") return <span>{text}</span>;
  const parts = text.split("CureBay");
  if (parts.length === 1) return <span>{text}</span>;
  return (
    <span>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {part}
          {i < parts.length - 1 && (
            <a
              href="https://curebay.com/services/book-an-appointment"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-[#01bd7f] hover:text-[#00a86b] underline cursor-pointer transition-colors"
            >
              CureBay
            </a>
          )}
        </React.Fragment>
      ))}
    </span>
  );
};

const Collapsible = ({ title, icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="space-y-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between rounded-lg border-2 p-6 hover:bg-gray-50 transition"
        style={{ borderColor: '#C9EEF8', backgroundColor: '#FFFFFF' }}
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--color-brand-primary)' }}>{title}</h2>
        </div>
        <ChevronDown
          className={`h-6 w-6 transition-transform ${isOpen ? "rotate-180" : ""}`}
          style={{ color: 'var(--color-brand-primary)' }}
        />
      </button>
      {isOpen && <div className="rounded-lg bg-white p-6 space-y-6">{children}</div>}
    </div>
  );
};

const SwapMealModal = ({
  isOpen,
  mealType,
  day,
  primaryMeal,
  onClose,
  onConfirm,
  alternativeMeal,
  originalMeal
}) => {
  const [selectedMeal, setSelectedMeal] = useState(null);

  if (!isOpen) return null;

  const options = [
    { id: 0, meal: alternativeMeal, label: "Alternative" },
    { id: 1, meal: originalMeal, label: "Original" }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold" style={{ color: "var(--color-brand-primary)" }}>
            Swap Meal
          </h3>
          <button onClick={onClose} className="transition hover:opacity-70" style={{ color: "#3A3A3A" }}>
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* CURRENT MEAL DETAILS */}
        <div className="mb-6">
          <p className="text-sm mb-2" style={{ color: "#3A3A3A" }}>
            You're swapping: {day} - {mealType}
          </p>
          <div className="p-3 rounded mb-4" style={{ backgroundColor: "#f3f4f6" }}>
            <p className="text-xs" style={{ color: "#3A3A3A" }}>Current meal:</p>
            <p className="text-sm font-medium line-clamp-2" style={{ color: "var(--color-brand-primary)" }}>
              {primaryMeal}
            </p>
          </div>
        </div>

        {/* OPTIONS */}
        <div className="mb-6">
          <p className="text-sm font-semibold mb-4" style={{ color: "var(--color-brand-primary)" }}>
            Choose another {mealType.toLowerCase()} to swap with:
          </p>

          <div className="space-y-3 max-h-48 overflow-y-auto">
            {options.map((option) => (
              <label
                key={option.id}
                className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition"
                style={{
                  border: selectedMeal === option.id ? "1.5px solid #00B5E2" : "1px solid #d1d5db",
                  backgroundColor: selectedMeal === option.id ? "#f0fbff" : "transparent"
                }}
              >
                <input
                  type="radio"
                  name="meal-swap"
                  value={option.id}
                  checked={selectedMeal === option.id}
                  onChange={() => setSelectedMeal(option.id)}
                  className="mt-1"
                  style={{ accentColor: "#00B5E2" }}
                />
                <div>
                  <p className="text-xs font-medium" style={{ color: "#3A3A3A" }}>
                    {option.label}
                  </p>
                  <span className="text-sm" style={{ color: "var(--color-brand-primary)" }}>
                    {option.meal}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* BUTTONS */}
        <div className="flex gap-3 mt-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg transition hover:opacity-80"
            style={{ border: "1px solid #d1d5db", color: "#3A3A3A" }}
          >
            Cancel
          </button>

          <button
            onClick={() => {
              if (selectedMeal !== null) {
                const selectedOption = options[selectedMeal];
                onConfirm(selectedOption.meal);
                setSelectedMeal(null);
              }
            }}
            disabled={selectedMeal === null}
            className="flex-1 px-4 py-2 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed text-white hover:opacity-90"
            style={{ background: "linear-gradient(90deg, #00CFA5, #00B5E2)" }}
          >
            Confirm Swap
          </button>
        </div>
      </div>
    </div>
  );
};

const WhatsAppShareModal = ({ isOpen, onClose, onConfirm, loading }) => {
  const [mobileNumber, setMobileNumber] = useState("");
  const [error, setError] = useState("");

  const handleShare = () => {
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!mobileNumber.trim()) {
      setError("Please enter a mobile number");
      return;
    }
    if (!phoneRegex.test(mobileNumber.trim())) {
      setError("Please enter a valid 10-digit mobile number starting with 6-9");
      return;
    }
    setError("");
    onConfirm(mobileNumber.trim());
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: "var(--color-brand-primary)" }}>
            <MessageCircle className="h-6 w-6" style={{ color: "var(--color-brand-primary)" }} />
            Share via WhatsApp
          </h3>
          <button onClick={onClose} className="transition hover:opacity-70" style={{ color: "#3A3A3A" }}>
            <X className="h-6 w-6" />
          </button>
        </div>

        <p className="text-sm mb-4" style={{ color: "#3A3A3A" }}>
          Enter the mobile number where you want to send the diet plan PDF.
        </p>

        {/* Input block */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1" style={{ color: "#3A3A3A" }}>
            Mobile Number (10 digits)
          </label>
          <input
            type="tel"
            value={mobileNumber}
            onChange={(e) => {
              setMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 10));
              setError("");
            }}
            placeholder="Enter mobile number"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none transition"
            style={{ borderColor: error ? "#ef4444" : "#d1d5db" }}
            onFocus={e => e.target.style.borderColor = "#00B5E2"}
            onBlur={e => e.target.style.borderColor = error ? "#ef4444" : "#d1d5db"}
          />
          {error && (
            <p className="text-xs mt-1" style={{ color: "#ef4444" }}>{error}</p>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-3 mt-4">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 border rounded-lg transition disabled:opacity-50 hover:bg-gray-50"
            style={{ borderColor: "#d1d5db", color: "#3A3A3A" }}
          >
            Cancel
          </button>
          <button
            onClick={handleShare}
            disabled={loading}
            className="flex-1 px-4 py-2 rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2 text-white hover:opacity-90"
            style={{ background: "linear-gradient(90deg, #00CFA5, #00B5E2)" }}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Sending...
              </>
            ) : (
              <>
                <MessageCircle className="h-4 w-4" />
                Send
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};


export default function DietResults() {
  const [data, setData] = useState(null);
  const [selectedDay, setSelectedDay] = useState("Monday");
  const [loading, setLoading] = useState(true);
  // FIX: added original field to swapModal state
  const [swapModal, setSwapModal] = useState({ isOpen: false, day: "", mealType: "", primaryMeal: "", alternative: "", original: "" });
  const [meals, setMeals] = useState({});
  const [pdfUrl, setPdfUrl] = useState("");
  const [patientId, setPatientId] = useState("");
  const [orderId, setOrderId] = useState("");
  const [defaultMobile, setDefaultMobile] = useState("");
  const [whatsappModal, setWhatsappModal] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const [pdfPending, setPdfPending] = useState(false);
  const [pdfCheckAttempts, setPdfCheckAttempts] = useState(0);
  const [checkingPdf, setCheckingPdf] = useState(false);
  const pdfCheckIntervalRef = useRef(null);
  const maxBackgroundChecks = 20;

  const checkPdfInBackground = async (orderid, language = "english") => {
    if (pdfCheckAttempts >= maxBackgroundChecks) {
      setPdfPending(false);
      if (pdfCheckIntervalRef.current) clearInterval(pdfCheckIntervalRef.current);
      return;
    }

    setCheckingPdf(true);
    setPdfCheckAttempts(prev => prev + 1);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SMARTDIET_BASEURL}check_pdf_complete?orderid=${orderid}`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );

      if (response.ok) {
        const result = await response.json();

        if (result.status === "ready" && result.pdf_url) {
          resolvePdfUrl(result.pdf_url).then(setPdfUrl);
          setPdfPending(false);
          setCheckingPdf(false);

          const storedData = JSON.parse(sessionStorage.getItem("dietResults"));
          const updatedData = {
            ...storedData,
            pdf_url: result.pdf_url,
            pdf_ready: true,
            pdf_pending: false,
            whatsapp_status: result.whatsapp_status || null
          };
          sessionStorage.setItem("dietResults", JSON.stringify(updatedData));
          setData(updatedData);

          sessionStorage.removeItem("pdfPendingOrderId");
          sessionStorage.removeItem("pdfPendingLanguage");

          if (pdfCheckIntervalRef.current) clearInterval(pdfCheckIntervalRef.current);

          setToast({ message: "🎉 Your PDF report is now ready!", type: "success" });
          return;
        }
      }
    } catch (error) {
      console.error("Background PDF check error:", error);
    }

    setCheckingPdf(false);
  };

  const handleManualPdfRetry = () => {
    if (orderId) {
      const language = sessionStorage.getItem("pdfPendingLanguage") || "english";
      setPdfCheckAttempts(0);
      checkPdfInBackground(orderId, language);
    }
  };

  useEffect(() => {
    const storedData = sessionStorage.getItem("dietResults");

    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        setData(parsedData);
        if (parsedData.pdf_url) {
          resolvePdfUrl(parsedData.pdf_url).then(setPdfUrl);
        } else {
          setPdfUrl("");
        }

        if (parsedData.patient_info) {
          setPatientId(parsedData.patient_info.patientid || "");
          setOrderId(parsedData.patient_info.orderid || "");
          setDefaultMobile(parsedData.patient_info.mobile || "");
        }

        if (parsedData.pdf_pending && !parsedData.pdf_ready) {
          setPdfPending(true);
          const pendingOrderId = sessionStorage.getItem("pdfPendingOrderId") || parsedData.patient_info?.orderid;
          const pendingLanguage = sessionStorage.getItem("pdfPendingLanguage") || "english";

          if (pendingOrderId) {
            setTimeout(() => {
              checkPdfInBackground(pendingOrderId, pendingLanguage);
              pdfCheckIntervalRef.current = setInterval(() => {
                checkPdfInBackground(pendingOrderId, pendingLanguage);
              }, 5000);
            }, 2000);
          }
        }

        if (parsedData.whatsapp_status === 200) {
          setToast({ message: "PDF successfully sent to WhatsApp!", type: "success" });
        }

        const mealsCopy = {};
        const weeklyPlan = parsedData.diet_result.weekly_plan || {};
        Object.keys(weeklyPlan).forEach(day => {
          mealsCopy[day] = {};
          Object.keys(weeklyPlan[day]).forEach(mealType => {
            if (mealType !== "Daily Nutrient Summary") {
              const mealData = weeklyPlan[day][mealType];
              mealsCopy[day][mealType] = mealData.primary || mealData;
            }
          });
        });
        setMeals(mealsCopy);
        const firstDay = Object.keys(weeklyPlan)[0];
        if (firstDay) setSelectedDay(firstDay);
      } catch (err) {
        console.error("Error parsing diet results:", err);
      }
    }

    setLoading(false);

    return () => {
      if (pdfCheckIntervalRef.current) clearInterval(pdfCheckIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // FIX: updated signature to accept original meal, stored in state
  const openSwapModal = (day, mealType, currentMeal, alternative, original) => {
    setSwapModal({
      isOpen: true,
      day,
      mealType,
      primaryMeal: currentMeal,
      alternative,
      original
    });
  };

  const handleSwapConfirm = (newMeal) => {
    const { day, mealType } = swapModal;
    setMeals(prev => ({
      ...prev,
      [day]: { ...prev[day], [mealType]: newMeal }
    }));
    // FIX: reset all fields including original
    setSwapModal({ isOpen: false, day: "", mealType: "", primaryMeal: "", alternative: "", original: "" });
  };

  const handleWhatsAppShare = async (mobileNumber) => {
    if (!pdfUrl) {
      setToast({ message: "PDF is still being generated. Please try again in a moment.", type: "error" });
      return;
    }

    setWhatsappLoading(true);
    try {
      const params = new URLSearchParams({
        patientid: patientId,
        orderid: orderId,
        pdf_url: pdfUrl,
        mobile: mobileNumber,
      });

      const response = await fetch(
        `${process.env.REACT_APP_SMARTDIET_BASEURL}/get_smart_diet_whatsapp?${params.toString()}`,
        { method: "GET" }
      );

      const data = await response.json();

      if (response.ok && data.status === "success") {
        setToast({ message: "PDF successfully sent to WhatsApp!", type: "success" });
        setWhatsappModal(false);
      } else {
        setToast({ message: data.message || "Failed to send WhatsApp message", type: "error" });
      }
    } catch (error) {
      setToast({ message: "Error sending PDF. Please try again.", type: "error" });
      console.error("Error sharing to WhatsApp:", error);
    } finally {
      setWhatsappLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ color: '#FF6B6B' }}>No diet plan available.</div>;

  const dietResult = data.diet_result;
  const weeklyPlan = dietResult.weekly_plan || {};
  const goalInsights = dietResult.goal_insights || {};

  const perspectiveText = typeof dietResult.perspective_analysis === 'string'
    ? dietResult.perspective_analysis
    : dietResult.perspective_analysis?.analysis || "N/A";

  const days = Object.keys(weeklyPlan);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pb-16 sm:pb-20"></div>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 md:px-8 pt-2 sm:pt-3 pb-6 sm:pb-8">

        {/* PDF Pending Banner */}
        {pdfPending && !pdfUrl && (
          <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-lg p-4 shadow-md">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                {checkingPdf ? (
                  <RefreshCw className="h-5 w-5 text-amber-600 animate-spin" />
                ) : (
                  <span className="text-2xl">⏳</span>
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-900 mb-1">
                  Your PDF Report is Being Generated
                </h3>
                <p className="text-xs text-amber-700 mb-2">
                  Your personalized diet plan is ready to view, but the PDF report is still being created.
                  This usually takes a few moments, especially for regional languages.
                  {checkingPdf && ` (Checking... ${pdfCheckAttempts}/${maxBackgroundChecks})`}
                </p>
                <button
                  onClick={handleManualPdfRetry}
                  disabled={checkingPdf}
                  className="text-xs text-amber-800 hover:text-amber-900 font-medium underline disabled:opacity-50 disabled:no-underline flex items-center gap-1"
                >
                  <RefreshCw className={`h-3 w-3 ${checkingPdf ? 'animate-spin' : ''}`} />
                  {checkingPdf ? 'Checking...' : 'Check Again'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hero Section */}
        <div
          className="relative mb-5 sm:mb-6 rounded-2xl shadow-md bg-white min-h-56 sm:min-h-64 lg:min-h-72 py-4 flex items-center"
          style={{ backgroundImage: `url("${diet}")`, backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "left center" }}
        >
          <div className="relative z-10 text-white px-4 sm:px-8 md:px-16 w-full flex flex-col justify-center items-start sm:items-end">
            <div className="max-w-full sm:max-w-lg text-left bg-white/70 rounded-xl p-4 sm:p-6">
              <h1
                className="text-2xl sm:text-3xl lg:text-4xl font-inter font-bold mb-2"
                style={{ color: 'var(--color-brand-primary)' }}
              >
                Smart <span style={{ color: 'var(--color-brand-green)' }}>Diet</span>
              </h1>
              <p className="text-sm sm:text-base mb-4 text-[#3A3A3A]">
                Your personalized path to a healthier lifestyle — smart, simple, and tailored just for you.
              </p>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                <button
                  onClick={() => {
                    if (pdfUrl) {
                      const link = document.createElement("a");
                      link.href = pdfUrl;
                      link.download = "Diet_Plan.pdf";
                      link.target = "_blank";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    } else if (pdfPending) {
                      setToast({ message: "PDF is still being generated. Please wait a moment and try again.", type: "error" });
                    } else {
                      setToast({ message: "PDF not available to download.", type: "error" });
                    }
                  }}
                  disabled={!pdfUrl && pdfPending}
                  className={`flex items-center gap-2 border-2 font-medium px-4 sm:px-5 py-2 rounded-full w-full sm:w-auto justify-center transition ${!pdfUrl && pdfPending ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
                  style={{ borderColor: 'var(--color-brand-primary)', color: 'var(--color-brand-primary)', backgroundColor: 'white' }}
                >
                  <Download className="h-4 w-4 sm:h-5 sm:w-5" />
                  {pdfPending && !pdfUrl ? 'Generating...' : 'Download'}
                </button>

                <div className="relative w-full sm:w-auto">
                  <button
                    onClick={() => setShareOpen(!shareOpen)}
                    className="flex items-center gap-2 font-medium px-4 sm:px-5 py-2 rounded-full shadow-md hover:opacity-90 w-full sm:w-auto justify-center"
                    style={{ backgroundColor: "var(--color-brand-primary)", color: "#FFFFFF" }}
                  >
                    <Share2 className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    Share
                  </button>

                  {shareOpen && (
                    <div
                      className="absolute top-full left-0 sm:right-0 w-full sm:w-48 bg-white rounded-lg shadow-lg py-2 z-50 mt-2"
                      style={{ border: "1px solid #e5e7eb", color: "#1f2937" }}
                    >
                      <button
                        onClick={() => {
                          if (pdfUrl) {
                            navigator.clipboard.writeText(pdfUrl);
                            setToast({ message: "Link copied to clipboard!", type: "success" });
                          } else {
                            setToast({ message: "PDF link not available yet", type: "error" });
                          }
                        }}
                        disabled={!pdfUrl}
                        className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm transition"
                        style={{ color: "#1f2937", opacity: !pdfUrl ? 0.5 : 1, cursor: !pdfUrl ? "not-allowed" : "pointer" }}
                        onMouseEnter={e => { if (pdfUrl) e.currentTarget.style.backgroundColor = "#f3f4f6"; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; }}
                      >
                        <Clipboard className="h-4 w-4" />
                        Copy Link
                      </button>

                      <button
                        onClick={() => {
                          if (pdfUrl) {
                            setWhatsappModal(true);
                          } else {
                            setToast({ message: "PDF is still being generated", type: "error" });
                          }
                        }}
                        disabled={!pdfUrl}
                        className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm transition"
                        style={{ color: "#1f2937", opacity: !pdfUrl ? 0.5 : 1, cursor: !pdfUrl ? "not-allowed" : "pointer" }}
                        onMouseEnter={e => { if (pdfUrl) e.currentTarget.style.backgroundColor = "#f3f4f6"; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; }}
                      >
                        <MessageCircle className="h-4 w-4" style={{ color: "#25D366" }} />
                        WhatsApp
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-4 sm:space-y-6">

          {/* Overview */}
          {(perspectiveText !== "N/A" ||
            (dietResult.recommendations && dietResult.recommendations.length > 0) ||
            (dietResult.bmi && dietResult.bmr) ||
            (dietResult.macros) ||
            (goalInsights.nutrient_status && Object.keys(goalInsights.nutrient_status).length > 0) ||
            (goalInsights.goal_summary)) && (
            <Collapsible title="Overview" icon="📊" defaultOpen>
              <div className="space-y-4 sm:space-y-6">

                {/* Perspective Analysis */}
                {perspectiveText !== "N/A" && (
                  <div className="p-3 sm:p-4 rounded" style={{ border: '1px solid #C9EEF8', boxShadow: '0px 4px 10px 0px #80DAF180', backgroundColor: '#FFFFFF' }}>
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <span className="text-base sm:text-lg">📊</span>
                      <h3 className="text-base sm:text-lg font-semibold" style={{ color: 'var(--color-brand-primary)' }}>Perspective Analysis</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">{renderCureBayHighlight(perspectiveText)}</p>
                  </div>
                )}

                {/* Actionable Recommendations */}
                {dietResult.recommendations && Array.isArray(dietResult.recommendations) && dietResult.recommendations.length > 0 && (
                  <div className="p-3 sm:p-4 rounded" style={{ border: '1px solid #C9EEF8', boxShadow: '0px 4px 10px 0px #80DAF180', backgroundColor: '#FFFFFF' }}>
                    <div className="flex items-center gap-2 sm:gap-3 mb-3">
                      <span className="text-base sm:text-lg">✅</span>
                      <h3 className="text-base sm:text-lg font-semibold" style={{ color: 'var(--color-brand-primary)' }}>Actionable Recommendations</h3>
                    </div>
                    <div className="flex flex-col gap-2">
                      {dietResult.recommendations
                        .map((rec, idx) => {
                          let recText = "";
                          if (typeof rec === "string") {
                            recText = rec;
                          } else if (typeof rec === "object") {
                            recText = rec[`recommendation_${idx + 1}`] ||
                              rec.recommendation ||
                              Object.values(rec).find(v => typeof v === 'string' && v.length > 10) ||
                              Object.values(rec)[0] || "";
                          }
                          return recText ? { recText, idx } : null;
                        })
                        .filter(Boolean)
                        .map(({ recText, idx }) => (
                          <div key={idx} className="flex gap-2 sm:gap-3" style={{ alignItems: 'baseline' }}>
                            <span
                              className="flex-shrink-0 rounded-full text-white flex items-center justify-center text-xs font-bold"
                              style={{ backgroundColor: '#42BA85', minWidth: '1.25rem', width: '1.25rem', height: '1.25rem' }}
                            >
                              ✓
                            </span>
                            <span className="text-sm text-gray-700 leading-relaxed">
                              {renderCureBayHighlight(recText)}
                            </span>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )}

                {/* Health & Energy Dashboard */}
                {(dietResult.bmi || dietResult.bmr) && (
                  <div className="p-3 sm:p-4 rounded" style={{ border: '1px solid #C9EEF8', boxShadow: '0px 4px 10px 0px #80DAF180', backgroundColor: '#FFFFFF' }}>
                    <div className="flex items-center gap-2 sm:gap-3 mb-3">
                      <span className="text-base sm:text-lg">❤️</span>
                      <h3 className="text-base sm:text-lg font-semibold text-[#004276]">Health & Energy Dashboard</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {dietResult.bmi && (() => {
                        const bmi = dietResult.bmi;
                        const bmiRounded = bmi.toFixed(2);
                        let category = "", bgColor = "", textColor = "";
                        if (bmi < 18.5) { category = "Underweight"; bgColor = "#FEE2E2"; textColor = "#DC2626"; }
                        else if (bmi < 25) { category = "Normal weight"; bgColor = "#DCFCE7"; textColor = "#15803D"; }
                        else if (bmi < 30) { category = "Overweight"; bgColor = "#FEE2E2"; textColor = "#DC2626"; }
                        else { category = "Obese"; bgColor = "#FEE2E2"; textColor = "#DC2626"; }
                        return (
                          <div className="bg-white p-3 sm:p-4 rounded-lg border" style={{ borderColor: '#BFDBFE' }}>
                            <p className="text-xs text-gray-600 mb-2 font-medium">BMI Report</p>
                            <p className="text-xl sm:text-2xl font-bold text-gray-900">{bmiRounded}</p>
                            <p className="text-xs text-gray-600 mt-1">
                              Category:{" "}
                              <span className="px-2 py-1 rounded" style={{ backgroundColor: bgColor, color: textColor }}>{category}</span>
                            </p>
                          </div>
                        );
                      })()}
                      {dietResult.bmr && (
                        <div className="bg-white p-3 sm:p-4 rounded-lg border" style={{ borderColor: '#BFDBFE' }}>
                          <p className="text-xs text-gray-600 mb-2 font-medium">BMR (Basal Metabolic Rate)</p>
                          <p className="text-xl sm:text-2xl font-bold text-gray-900">{dietResult.bmr?.toFixed(0)}</p>
                          <p className="text-xs text-gray-600 mt-1">kcal per day</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Daily Fuel Plan */}
                {dietResult.macros && (
                  <div className="p-3 sm:p-4 rounded" style={{ border: '1px solid #C9EEF8', boxShadow: '0px 4px 10px 0px #80DAF180', backgroundColor: '#FFFFFF' }}>
                    <div className="flex items-center gap-2 sm:gap-3 mb-3">
                      <span className="text-base sm:text-lg">⚡</span>
                      <h3 className="text-base sm:text-lg font-semibold" style={{ color: 'var(--color-brand-primary)' }}>Your Daily Fuel Plan</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full table-fixed text-xs sm:text-sm">
                        <thead>
                          <tr style={{ borderBottom: '2px solid #CFFAFE' }}>
                            <th className="p-2 sm:p-3 text-left font-bold text-gray-800 w-1/3">Type</th>
                            <th className="p-2 sm:p-3 text-left font-bold text-gray-800 w-1/3">Intake</th>
                            <th className="p-2 sm:p-3 text-left font-bold text-gray-800 w-1/3 hidden sm:table-cell">Support</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dietResult.macros.protein_g && (
                            <tr style={{ borderBottom: '1px solid #E0F2FE' }}>
                              <td className="p-2 sm:p-3 font-medium">Protein</td>
                              <td className="p-2 sm:p-3 font-semibold">{dietResult.macros.protein_g.toFixed(1)}g<div className="text-[11px] text-gray-500 sm:hidden mt-1">• Muscle build & recovery</div></td>
                              <td className="p-2 sm:p-3 text-xs hidden sm:table-cell">• Muscle build & recovery</td>
                            </tr>
                          )}
                          {dietResult.macros.carbs_g && (
                            <tr style={{ borderBottom: '1px solid #E0F2FE' }}>
                              <td className="p-2 sm:p-3 font-medium">Carbs</td>
                              <td className="p-2 sm:p-3 font-semibold">{dietResult.macros.carbs_g.toFixed(1)}g<div className="text-[11px] text-gray-500 sm:hidden mt-1">• Energy & fuel</div></td>
                              <td className="p-2 sm:p-3 text-xs hidden sm:table-cell">• Energy & fuel</td>
                            </tr>
                          )}
                          {dietResult.macros.calories && (
                            <tr style={{ borderBottom: '1px solid #E0F2FE' }}>
                              <td className="p-2 sm:p-3 font-medium">Calories</td>
                              <td className="p-2 sm:p-3 font-semibold">{dietResult.macros.calories.toFixed(0)} kcal<div className="text-[11px] text-gray-500 sm:hidden mt-1">• Total daily energy intake</div></td>
                              <td className="p-2 sm:p-3 text-xs hidden sm:table-cell">• Total daily energy intake</td>
                            </tr>
                          )}
                          {dietResult.macros.fat_g && (
                            <tr style={{ borderBottom: '1px solid #E0F2FE' }}>
                              <td className="p-2 sm:p-3 font-medium">Fats</td>
                              <td className="p-2 sm:p-3 font-semibold">{dietResult.macros.fat_g.toFixed(1)}g<div className="text-[11px] text-gray-500 sm:hidden mt-1">• Cell structure</div></td>
                              <td className="p-2 sm:p-3 text-xs hidden sm:table-cell">• Cell structure</td>
                            </tr>
                          )}
                          {dietResult.macros.fiber_g && (
                            <tr style={{ borderBottom: '1px solid #E0F2FE' }}>
                              <td className="p-2 sm:p-3 font-medium">Fiber</td>
                              <td className="p-2 sm:p-3 font-semibold">{dietResult.macros.fiber_g.toFixed(1)}g<div className="text-[11px] text-gray-500 sm:hidden mt-1">• Digestive health</div></td>
                              <td className="p-2 sm:p-3 text-xs hidden sm:table-cell">• Digestive health</td>
                            </tr>
                          )}
                          {dietResult.macros.water_ml && (
                            <tr>
                              <td className="p-2 sm:p-3 font-medium">Water</td>
                              <td className="p-2 sm:p-3 font-semibold">{dietResult.macros.water_ml} ml<div className="text-[11px] text-gray-500 sm:hidden mt-1">• Hydration support</div></td>
                              <td className="p-2 sm:p-3 text-xs hidden sm:table-cell">• Hydration support</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Sources of Nutrients */}
                {goalInsights.nutrient_status && Object.keys(goalInsights.nutrient_status).length > 0 && (
                  <div className="p-3 sm:p-4 rounded" style={{ border: '1px solid #C9EEF8', boxShadow: '0px 4px 10px 0px #80DAF180', backgroundColor: '#FFFFFF' }}>
                    <div className="flex items-center gap-2 sm:gap-3 mb-4">
                      <span className="text-base sm:text-lg">💊</span>
                      <h3 className="text-base sm:text-lg font-semibold text-[#004276]">Sources of Nutrients</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      {Object.entries(goalInsights.nutrient_status).map(([nutrient, info]) => (
                        <div key={nutrient} className="bg-white p-3 sm:p-4 rounded-lg border" style={{ borderColor: '#C9EEF8' }}>
                          <div className="flex justify-between items-start mb-2">
                            <p className="font-semibold text-sm sm:text-base text-gray-800">{nutrient}</p>
                          </div>
                          <div className="text-xs sm:text-sm">
                            <p className="font-semibold text-gray-700 mb-2">Sources:</p>
                            <ul className="text-gray-600 space-y-1">
                              {info.food_sources && Array.isArray(info.food_sources) ? (
                                info.food_sources.slice(0, 3).map((source, idx) => (
                                  <li key={idx} className="flex gap-1"><span>•</span><span>{source}</span></li>
                                ))
                              ) : (
                                <li>No sources available</li>
                              )}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Goal Summary */}
                {goalInsights.goal_summary && (
                  <div className="backdrop-blur-md bg-white/60 border border-blue-200/50 p-4 sm:p-5 rounded-2xl shadow-lg">
                    <div className="flex items-center gap-2 sm:gap-3 mb-3">
                      <span className="text-base sm:text-xl">🎯</span>
                      <h3 className="text-base sm:text-lg font-semibold text-blue-800">Goal Summary</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">{goalInsights.goal_summary}</p>
                  </div>
                )}
              </div>
            </Collapsible>
          )}

          {/* Weekly Plan */}
          {days.length > 0 && (
            <Collapsible title="Weekly Plan" icon="📅" defaultOpen>
              <div className="space-y-4 sm:space-y-6">
                {/* Day Selection */}
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
                  {days.map((day) => (
                    <button
                      key={day}
                      onClick={() => setSelectedDay(day)}
                      className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition border flex-shrink-0 ${
                        selectedDay === day
                          ? "bg-[#004276] text-white border-[#004276]"
                          : "bg-white text-[#444] hover:bg-gray-100 border-gray-300"
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>

                <div className="space-y-3 sm:space-y-4">
                  {weeklyPlan[selectedDay] &&
                    Object.entries(weeklyPlan[selectedDay]).map(([mealType, mealData]) => {
                      if (mealType === "Daily Nutrient Summary") {
                        return (
                          <div key={mealType}>
                            <div className="bg-[#E8F5E9] p-3 sm:p-4 rounded-lg border-l-4 border-[#2E7D32]">
                              <h4 className="font-semibold text-[#1B5E20] mb-2 text-sm sm:text-base">Daily Nutrient Summary</h4>
                              <p className="text-xs sm:text-sm text-[#333]">
                                {typeof mealData === "string" ? mealData : mealData.summary || mealData}
                              </p>
                            </div>
                            <div className="mt-4 flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
                              {days.map((day) => (
                                <button
                                  key={`after-summary-${day}`}
                                  onClick={() => setSelectedDay(day)}
                                  className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition border flex-shrink-0 ${
                                    selectedDay === day
                                      ? "bg-[#004276] text-white border-[#004276]"
                                      : "bg-white text-[#444] hover:bg-gray-100 border-gray-300"
                                  }`}
                                >
                                  {day}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      }

                      const primaryMeal = mealData.primary || mealData;
                      // FIX: correctly read alternative from dish.alternative
                      const alternativeMeal = mealData.dish?.alternative || mealData.alternative || "No alternative";
                      const currentMeal = meals[selectedDay]?.[mealType] || primaryMeal;

                      return (
                        <div key={mealType} className="border border-gray-300 rounded-lg overflow-hidden">
                          <div className="bg-[#E3F2FD] p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <Utensils className="h-4 w-4 sm:h-5 sm:w-5 text-[#0277BD]" />
                              <h4 className="font-semibold text-[#01579B] text-sm sm:text-base">{mealType}</h4>
                            </div>
                            <button
                              onClick={() =>
                                // FIX: pass primaryMeal as the 5th arg (original)
                                openSwapModal(selectedDay, mealType, currentMeal, alternativeMeal, primaryMeal)
                              }
                              className="text-[#0277BD] hover:text-[#01579B] font-medium text-xs sm:text-sm border border-[#0277BD] px-3 py-1 rounded hover:bg-[#E1F5FE] whitespace-nowrap"
                            >
                              ⟲ Swap
                            </button>
                          </div>
                          <div className="p-3 sm:p-4 space-y-3">
                            <div className="bg-[#FFF8E1] p-3 rounded border-l-4 border-[#FFB300]">
                              <div className="text-xs font-medium text-[#FF8F00] mb-1">Primary Option</div>
                              <p className="text-xs sm:text-sm text-[#333]">{currentMeal}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </Collapsible>
          )}

          {/* Holistic Insights */}
          {((dietResult.mental_health_support && dietResult.mental_health_support.length > 0) ||
            (goalInsights.smart_swaps && goalInsights.smart_swaps.length > 0) ||
            (goalInsights.habit_focus && goalInsights.habit_focus.length > 0) ||
            (goalInsights.recipe)) && (
            <Collapsible title="Holistic Insights" icon="🧘">
              <div className="space-y-4 sm:space-y-6">

                {/* Mental Health Support */}
                {dietResult.mental_health_support && Array.isArray(dietResult.mental_health_support) && dietResult.mental_health_support.length > 0 && (
                  <div className="border-l-4 border-[#004276] bg-[#EAF3FA] p-3 sm:p-4 rounded">
                    <h3 className="text-base sm:text-lg font-semibold text-[#004276] mb-3 sm:mb-4">Mental Health & Wellness</h3>
                    <div className="space-y-2 sm:space-y-3">
                      {dietResult.mental_health_support.map((tip, idx) => (
                        <div key={idx} className="flex gap-2 sm:gap-3">
                          <svg className="h-4 w-4 sm:h-5 sm:w-5 text-[#004276] flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <p className="text-xs sm:text-sm text-[#444]">{tip}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Smart Swaps */}
                {goalInsights.smart_swaps && Array.isArray(goalInsights.smart_swaps) && goalInsights.smart_swaps.length > 0 && (
                  <div className="border-l-4 border-[#43A047] bg-[#E8F5E9] p-3 sm:p-4 rounded">
                    <h3 className="text-base sm:text-lg font-semibold text-[#2E7D32] mb-3 sm:mb-4">Smart Food Swaps</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      {goalInsights.smart_swaps.map((swap, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white p-3 rounded border border-[#A5D6A7]">
                          <svg className="h-4 w-4 sm:h-5 sm:w-5 text-[#2E7D32] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                          <span className="text-xs sm:text-sm text-[#444]">{swap}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Habit Goals */}
                {goalInsights.habit_focus && Array.isArray(goalInsights.habit_focus) && goalInsights.habit_focus.length > 0 && (
                  <div className="border-l-4 border-[#1E88E5] bg-[#E3F2FD] p-3 sm:p-4 rounded">
                    <h3 className="text-base sm:text-lg font-semibold text-[#1565C0] mb-3 sm:mb-4">Daily Habit Goals</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {goalInsights.habit_focus.map((habit, idx) => (
                        <div key={idx} className="bg-white p-3 sm:p-4 rounded-lg border border-[#90CAF9]">
                          <div className="text-xs sm:text-sm font-medium text-[#1976D2] mb-2">{habit.metric}</div>
                          <p className="text-xs sm:text-sm text-[#444]">{habit.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recipe */}
                {goalInsights.recipe && (
                  <div className="border-l-4 border-[#FB8C00] bg-[#FFF3E0] p-3 sm:p-4 rounded">
                    <h3 className="text-base sm:text-lg font-semibold text-[#EF6C00] mb-3 sm:mb-4">
                      Featured Recipe: {goalInsights.recipe.title}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <h4 className="font-semibold mb-2 sm:mb-3 text-[#333] text-sm sm:text-base">Ingredients</h4>
                        <ul className="space-y-1 sm:space-y-2">
                          {goalInsights.recipe.ingredients?.map((ingredient, idx) => (
                            <li key={idx} className="text-xs sm:text-sm flex gap-2 text-[#444]">
                              <span className="text-[#FB8C00]">•</span>
                              <span>{ingredient}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2 sm:mb-3 text-[#333] text-sm sm:text-base">Instructions</h4>
                        <ol className="space-y-1 sm:space-y-2">
                          {goalInsights.recipe.instructions?.map((instruction, idx) => (
                            <li key={idx} className="text-xs sm:text-sm flex gap-2 text-[#444]">
                              <span className="font-medium text-[#FB8C00]">{idx + 1}.</span>
                              <span>{instruction}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                    <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-[#FFCC80] grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                      <div><div className="text-xs text-[#666]">Calories</div><div className="font-semibold text-[#333] text-sm sm:text-base">{goalInsights.recipe.nutrition_per_serving?.calories}</div></div>
                      <div><div className="text-xs text-[#666]">Protein</div><div className="font-semibold text-[#333] text-sm sm:text-base">{goalInsights.recipe.nutrition_per_serving?.protein}</div></div>
                      <div><div className="text-xs text-[#666]">Carbs</div><div className="font-semibold text-[#333] text-sm sm:text-base">{goalInsights.recipe.nutrition_per_serving?.carbs}</div></div>
                      <div><div className="text-xs text-[#666]">Fat</div><div className="font-semibold text-[#333] text-sm sm:text-base">{goalInsights.recipe.nutrition_per_serving?.fat}</div></div>
                    </div>
                    <div className="mt-2 text-xs text-[#666]">
                      ⏱ {goalInsights.recipe.cooking_time} | 🍽 {goalInsights.recipe.servings} servings
                    </div>
                  </div>
                )}
              </div>
            </Collapsible>
          )}

          {/* Medical Report Highlights */}
          {dietResult.pdf_report && Array.isArray(dietResult.pdf_report) && dietResult.pdf_report.length > 0 && (
            <Collapsible title="Medical Report Highlights" icon="📋">
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-base sm:text-lg">🔍</span>
                  <h4 className="font-semibold text-[#333] text-sm sm:text-base">Key Findings</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {dietResult.pdf_report.map((report, index) => {
                    let bgColor = "#E8F5E9", borderColor = "#43A047", tagBg = "#C8E6C9", tagText = "#1B5E20";
                    if (report.status === "High") { bgColor = "#FFF8E1"; borderColor = "#FFB300"; tagBg = "#FFE082"; tagText = "#FF6F00"; }
                    else if (report.status === "Low") { bgColor = "#FFEBEE"; borderColor = "#E53935"; tagBg = "#FFCDD2"; tagText = "#B71C1C"; }
                    return (
                      <div key={index} className="p-3 sm:p-4 rounded-lg border-l-4 transition-all" style={{ backgroundColor: bgColor, borderColor }}>
                        <div className="flex justify-between items-start mb-3">
                          <h5 className="font-semibold text-[#333] text-xs sm:text-sm pr-2">{report.parameter}</h5>
                          <span className="text-xs px-2 py-1 rounded font-medium whitespace-nowrap" style={{ backgroundColor: tagBg, color: tagText }}>{report.status}</span>
                        </div>
                        <div className="mb-3 sm:mb-4">
                          <div className="text-xl sm:text-2xl font-bold text-[#111]">{report.value}</div>
                          <div className="text-xs text-[#666] mt-1"><strong>Reference:</strong> {report.reference_range}</div>
                        </div>
                        <div className="space-y-2 sm:space-y-3 text-xs border-t border-[#DDD] pt-3">
                          {report.avoid && <div><strong className="text-[#444] block mb-1">❌ Avoid:</strong><p className="text-[#555] line-clamp-2">{report.avoid}</p></div>}
                          {report.recommend && <div><strong className="text-[#444] block mb-1">✅ Recommend:</strong><p className="text-[#555] line-clamp-2">{report.recommend}</p></div>}
                          {report.food_sources && <div><strong className="text-[#444] block mb-1">🍽️ Food Sources:</strong><p className="text-[#555] line-clamp-2">{report.food_sources}</p></div>}
                        </div>
                        {report.test_description && (
                          <div className="mt-3 pt-3 border-t border-[#DDD]">
                            <p className="text-xs text-[#666] italic"><span className="font-bold">About Test:</span> {report.test_description}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Collapsible>
          )}
        </div>
      </div>

      {/* Modals */}
      <SwapMealModal
        isOpen={swapModal.isOpen}
        day={swapModal.day}
        mealType={swapModal.mealType}
        primaryMeal={swapModal.primaryMeal}
        alternativeMeal={swapModal.alternative}
        originalMeal={swapModal.original}
        onClose={() => setSwapModal({ isOpen: false, day: "", mealType: "", primaryMeal: "", alternative: "", original: "" })}
        onConfirm={handleSwapConfirm}
      />

      <WhatsAppShareModal
        isOpen={whatsappModal}
        onClose={() => setWhatsappModal(false)}
        onConfirm={handleWhatsAppShare}
        loading={whatsappLoading}
      />

      {/* Toast */}
      {toast && (
        <div className="fixed top-16 sm:top-20 right-4 z-50 animate-in fade-in slide-in-from-right-4 duration-300 max-w-[calc(100vw-2rem)] sm:max-w-sm">
          <div
            className={`${
              toast.type === "success"
                ? "bg-lightGreen border-secondaryColor text-quantumPrimary"
                : "bg-lghtOrange border-danger text-danger"
            } border-l-4 rounded-lg shadow-lg p-3 sm:p-4 flex items-center gap-2 sm:gap-3`}
          >
            <span className="text-base sm:text-lg">{toast.type === "success" ? "✓" : "⚠"}</span>
            <p className="text-xs sm:text-sm font-medium flex-1 text-quantumPrimary">{toast.message}</p>
            <button onClick={() => setToast(null)} className="ml-2 opacity-70 hover:opacity-100 text-textGray">✕</button>
          </div>
        </div>
      )}
    </div>
  );
}